import { useEffect } from 'react';

/**
 * ImageProtection — an invisible, non-intrusive discouragement layer for
 * artwork imagery on the public site. It:
 *
 *   • blocks right-click on <img> elements,
 *   • blocks native drag-and-drop of <img> elements,
 *   • sets `draggable="false"` on every current + future <img>,
 *
 * paired with CSS in index.css that disables text/image selection and
 * suppresses the iOS long-press "Save Image" callout on <img>.
 *
 * This is a mount-once component. Mount it inside the PUBLIC chrome only —
 * admins editing content in the CMS still need normal image behaviour.
 *
 * Known limits (documented for the collector-facing team): no browser
 * technique can prevent screenshots or a technically motivated user with
 * DevTools. The goal here is quiet friction, not enforcement.
 */
export default function ImageProtection() {
    useEffect(() => {
        const isImg = (el) => el && el.tagName === 'IMG';

        const onContext = (e) => {
            if (isImg(e.target)) e.preventDefault();
        };
        const onDragStart = (e) => {
            if (isImg(e.target)) e.preventDefault();
        };

        // Apply `draggable="false"` and referrerPolicy to every current <img>
        // and any that get inserted later (React re-renders, lazy loads, etc.).
        const harden = (img) => {
            if (!img || img.dataset.protected === '1') return;
            img.setAttribute('draggable', 'false');
            if (!img.getAttribute('referrerpolicy')) {
                img.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
            }
            img.dataset.protected = '1';
        };
        document.querySelectorAll('img').forEach(harden);

        const mo = new MutationObserver((mutations) => {
            for (const m of mutations) {
                m.addedNodes.forEach((node) => {
                    if (isImg(node)) harden(node);
                    else if (node.querySelectorAll) node.querySelectorAll('img').forEach(harden);
                });
            }
        });
        mo.observe(document.body, { childList: true, subtree: true });

        document.addEventListener('contextmenu', onContext);
        document.addEventListener('dragstart', onDragStart);

        return () => {
            document.removeEventListener('contextmenu', onContext);
            document.removeEventListener('dragstart', onDragStart);
            mo.disconnect();
        };
    }, []);

    return null;
}
